import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Skip the /auth/me probe when there's no session token (clean console on /login)
    if (!localStorage.getItem("ajar_token")) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_pin) return data;
    if (data.token) localStorage.setItem("ajar_token", data.token);
    setUser(data.user);
    return data;
  };

  const loginPin = async (pinToken, pin) => {
    const { data } = await api.post("/auth/login/pin", {
      pin_token: pinToken,
      pin,
    });
    if (data.token) localStorage.setItem("ajar_token", data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Non-blocking: a failed logout call shouldn't prevent the client-side
      // session from being cleared. Log for diagnostics.
      console.warn("AuthContext.logout: server logout failed", e);
    }
    localStorage.removeItem("ajar_token");
    sessionStorage.removeItem("ajar_pin_prompt_dismissed");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginPin, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
